import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  NumberInput,
  NumberInputField,
  VStack,
  HStack,
  SimpleGrid,
  Box,
  Heading,
  Text,
  Divider,
} from '@chakra-ui/react';
import { User, MetricName, Rock, GoalOrientation, Metric } from '../../types';

interface AddMetricModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: any) => Promise<void>;
  currentUserId?: string;
  users: User[];
  rocks?: Rock[];
  metricNames?: MetricName[];
  metricToEdit?: Metric | null;
}

/**
 * Add Metric Modal Component
 * Uses Formik for form state and Yup for validation
 * Updated to support EOS 13-week scorecard fields
 */
export function AddMetricModal({
  isOpen,
  onClose,
  onSave,
  currentUserId,
  users,
  rocks = [],
  metricNames = [],
  metricToEdit = null,
}: AddMetricModalProps) {
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  const quarterMap: { [key: number]: 'Q1' | 'Q2' | 'Q3' | 'Q4' } = {
    1: 'Q1',
    2: 'Q2',
    3: 'Q3',
    4: 'Q4',
  };

  // Validation schema using Yup
  const validationSchema = Yup.object({
    metricNameId: Yup.string().required('Metric name is required'),
    customMetricName: Yup.string()
      .when('metricNameId', {
        is: 'other',
        then: (schema) => schema
          .required('Custom metric name is required')
          .min(1, 'Custom metric name must be at least 1 character')
          .max(100, 'Custom metric name must be at most 100 characters'),
        otherwise: (schema) => schema.notRequired(),
      }),
    measurable: Yup.string()
      .required('Measurable is required')
      .min(3, 'Measurable must be at least 3 characters'),
    quarter: Yup.string().oneOf(['Q1', 'Q2', 'Q3', 'Q4']).required('Quarter is required'),
    year: Yup.number()
      .required('Year is required')
      .min(2020, 'Year must be at least 2020')
      .max(2100, 'Year must be at most 2100'),
    ownerId: Yup.string().nullable(),
    category: Yup.string()
      .oneOf(['Sales', 'Marketing', 'Operations', 'Finance', 'Technology', 'Customer Service', 'Leadership', '', null], 'Invalid category')
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' || originalValue === null ? null : value;
      }),
    collaboratorId: Yup.string().nullable(),
    // Updated Schemas for Scorecard
    goalOrientation: Yup.string().oneOf(['gte', 'lte', 'eq', 'range']).required('Goal orientation is required'),
    weeklyGoal: Yup.number().nullable().min(0, 'Goal must be >= 0'),
    unit: Yup.string().nullable(),
    goalRangeMin: Yup.number().nullable().when('goalOrientation', {
      is: 'range',
      then: (schema) => schema.required('Min range is required'),
    }),
    goalRangeMax: Yup.number().nullable().when('goalOrientation', {
      is: 'range',
      then: (schema) => schema
        .required('Max range is required')
        .test('min-max', 'Max must be >= Min', function(value) {
          const { goalRangeMin } = this.parent;
          return value >= goalRangeMin;
        }),
    }),
  });

  const initialValues = {
    metricNameId: metricToEdit?.metricNameId || '',
    customMetricName: metricToEdit?.metricNameId === 'other' ? metricToEdit.metricName : '',
    measurable: metricToEdit?.measurable || '',
    quarter: (metricToEdit?.quarter || quarterMap[currentQuarter]) as 'Q1' | 'Q2' | 'Q3' | 'Q4',
    year: metricToEdit?.year || currentYear,
    ownerId: metricToEdit?.ownerId || currentUserId || '',
    category: (metricToEdit?.category || null) as 'Sales' | 'Marketing' | 'Operations' | 'Finance' | 'Technology' | 'Customer Service' | 'Leadership' | null,
    collaboratorId: metricToEdit?.collaboratorId || null as string | null,
    measurementType: metricToEdit?.measurementType || 'count' as 'currency' | 'count' | 'percentage',
    
    // EOS Scorecard Goals
    goalOrientation: metricToEdit?.goalOrientation || 'gte' as GoalOrientation,
    weeklyGoal: metricToEdit?.weeklyGoal || metricToEdit?.quarterlyGoal || 0,
    unit: metricToEdit?.unit || '',
    goalRangeMin: metricToEdit?.goalRangeMin || 0,
    goalRangeMax: metricToEdit?.goalRangeMax || 0,
    
    relatedRockId: metricToEdit?.relatedRockId || null as string | null,
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onSave({
        ...values,
        category: values.category === '' ? null : values.category,
        ownerId: values.ownerId === '' ? null : (values.ownerId || currentUserId),
        collaboratorId: values.collaboratorId === '' ? null : values.collaboratorId,
        relatedRockId: values.relatedRockId === '' ? null : values.relatedRockId,
        year: parseInt(values.year),
        weeklyGoal: parseFloat(values.weeklyGoal) || 0,
        goalRangeMin: values.goalOrientation === 'range' ? parseFloat(values.goalRangeMin) : null,
        goalRangeMax: values.goalOrientation === 'range' ? parseFloat(values.goalRangeMax) : null,
        // Backward compatibility
        quarterlyGoal: parseFloat(values.weeklyGoal) || 0, 
        timePeriodType: 'weekly',
        id: metricToEdit?.id,
      });
      onClose();
    } catch (error) {
      console.error('Error saving metric:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{metricToEdit ? 'Edit Scorecard Metric' : 'Add New Scorecard Metric'}</ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  
                  {/* METRIC IDENTITY SECTION */}
                  <Box>
                    <Heading size="xs" textTransform="uppercase" color="gray.500" mb={3}>
                      Metric Details
                    </Heading>
                    <VStack spacing={4}>
                      {/* Metric Name */}
                      <Field name="metricNameId">
                        {({ field, form }: any) => (
                          <FormControl isInvalid={form.errors.metricNameId && form.touched.metricNameId}>
                            <FormLabel fontSize="sm">Metric Name</FormLabel>
                            <Select {...field} value={field.value || ''} size="sm">
                              <option value="">-- Select Metric --</option>
                              {metricNames && metricNames.length > 0 ? (
                                metricNames.map((name) => (
                                  <option key={name.id} value={name.id}>
                                    {name.name}
                                  </option>
                                ))
                              ) : null}
                              <option value="other">Other (Custom)</option>
                            </Select>
                            <FormErrorMessage>{form.errors.metricNameId}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {values.metricNameId === 'other' && (
                        <Field name="customMetricName">
                          {({ field, form }: any) => (
                            <FormControl isInvalid={form.errors.customMetricName && form.touched.customMetricName}>
                              <FormLabel fontSize="sm">Custom Metric Name</FormLabel>
                              <Input {...field} placeholder="Enter name..." size="sm" />
                              <FormErrorMessage>{form.errors.customMetricName}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      )}

                      {/* Measurable */}
                      <Field name="measurable">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched}>
                            <FormLabel fontSize="sm">Measurable (Description)</FormLabel>
                            <Textarea
                              {...field}
                              placeholder="e.g. Number of qualified leads per week"
                              rows={2}
                              size="sm"
                            />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {/* Category & Owner Grid */}
                      <SimpleGrid columns={2} spacing={4} width="100%">
                        <Field name="category">
                          {({ field }: any) => (
                            <FormControl>
                              <FormLabel fontSize="sm">Category</FormLabel>
                              <Select {...field} value={field.value || ''} size="sm">
                                <option value="">None</option>
                                {['Sales', 'Marketing', 'Operations', 'Finance', 'Technology', 'Customer Service', 'Leadership'].map(cat => (
                                  <option key={cat} value={cat}>{cat}</option>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="ownerId">
                          {({ field }: any) => (
                            <FormControl>
                              <FormLabel fontSize="sm">Owner</FormLabel>
                              <Select {...field} value={field.value || ''} size="sm">
                                <option value="">None</option>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>{user.name}</option>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Field>
                      </SimpleGrid>


                    </VStack>
                  </Box>

                  <Divider />

                  {/* GOAL CONFIGURATION SECTION */}
                  <Box bg="blue.50" p={4} borderRadius="md" border="1px" borderColor="blue.100">
                    <Heading size="xs" textTransform="uppercase" color="blue.600" mb={3}>
                      Goal Configuration
                    </Heading>
                    
                    <VStack spacing={4} align="stretch">
                      {/* Orientation & Unit Grid */}
                      <SimpleGrid columns={2} spacing={4}>
                         <Field name="goalOrientation">
                          {({ field }: any) => (
                            <FormControl>
                              <FormLabel fontSize="sm">Goal Orientation</FormLabel>
                              <Select {...field} size="sm" bg="white">
                                <option value="gte">Greater Than or Equal (≥)</option>
                                <option value="lte">Less Than or Equal (≤)</option>
                                <option value="eq">Equal To (=)</option>
                                <option value="range">Range (Min - Max)</option>
                              </Select>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="measurementType">
                          {({ field }: any) => (
                            <FormControl>
                              <FormLabel fontSize="sm">Type</FormLabel>
                              <Select {...field} size="sm" bg="white">
                                <option value="count">Count (Number)</option>
                                <option value="currency">Currency ($)</option>
                                <option value="percentage">Percentage (%)</option>
                              </Select>
                            </FormControl>
                          )}
                        </Field>
                      </SimpleGrid>

                      {/* Goal Values */}
                      {values.goalOrientation === 'range' ? (
                        <SimpleGrid columns={2} spacing={4}>
                           <Field name="goalRangeMin">
                            {({ field, form, meta }: any) => (
                              <FormControl isInvalid={meta.error && meta.touched}>
                                <FormLabel fontSize="sm">Min Value</FormLabel>
                                <NumberInput
                                  size="sm"
                                  bg="white"
                                  value={field.value}
                                  onChange={(_, val) => form.setFieldValue(field.name, val)}
                                >
                                  <NumberInputField />
                                </NumberInput>
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                           <Field name="goalRangeMax">
                            {({ field, form, meta }: any) => (
                              <FormControl isInvalid={meta.error && meta.touched}>
                                <FormLabel fontSize="sm">Max Value</FormLabel>
                                <NumberInput
                                  size="sm"
                                  bg="white"
                                  value={field.value}
                                  onChange={(_, val) => form.setFieldValue(field.name, val)}
                                >
                                  <NumberInputField />
                                </NumberInput>
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </SimpleGrid>
                      ) : (
                         <Field name="weeklyGoal">
                          {({ field, form, meta }: any) => (
                            <FormControl isInvalid={meta.error && meta.touched}>
                              <FormLabel fontSize="sm">Weekly Goal Target</FormLabel>
                              <NumberInput
                                size="sm"
                                bg="white"
                                value={field.value}
                                onChange={(valStr, valNum) => {
                                  form.setFieldValue(field.name, valStr === '' ? 0 : valNum);
                                }}
                              >
                                <NumberInputField />
                              </NumberInput>
                              <FormErrorMessage>{meta.error}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      )}
                      
                      <Field name="unit">
                        {({ field, meta }: any) => (
                          <FormControl>
                            <FormLabel fontSize="sm">Unit Label (Optional)</FormLabel>
                            <Input {...field} placeholder="e.g. calls, meetings, widgets" size="sm" bg="white" />
                          </FormControl>
                        )}
                      </Field>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* TIMING SELECTION */}
                   <SimpleGrid columns={2} spacing={4}>
                    <Field name="quarter">
                      {({ field, meta }: any) => (
                        <FormControl isInvalid={meta.error && meta.touched}>
                          <FormLabel fontSize="sm">Quarter</FormLabel>
                          <Select {...field} size="sm">
                            <option value="Q1">Q1</option>
                            <option value="Q2">Q2</option>
                            <option value="Q3">Q3</option>
                            <option value="Q4">Q4</option>
                          </Select>
                        </FormControl>
                      )}
                    </Field>

                    <Field name="year">
                      {({ field, form, meta }: any) => (
                        <FormControl isInvalid={meta.error && meta.touched}>
                          <FormLabel fontSize="sm">Year</FormLabel>
                          <NumberInput
                            size="sm"
                            value={field.value}
                            onChange={(_, val) => form.setFieldValue(field.name, val)}
                            min={2020}
                            max={2100}
                          >
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                      )}
                    </Field>
                  </SimpleGrid>
                   
                   {/* RELATED ROCK */}
                    {rocks.length > 0 && (
                    <Field name="relatedRockId">
                      {({ field }: any) => (
                        <FormControl>
                          <FormLabel fontSize="sm">Related Rock (Optional)</FormLabel>
                          <Select {...field} value={field.value || ''} size="sm">
                            <option value="">None</option>
                            {rocks.map((rock) => (
                              <option key={rock.id} value={rock.id}>
                                {rock.rock}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Field>
                  )}

                </VStack>
              </ModalBody>

              <ModalFooter bg="gray.50">
                <Button variant="ghost" mr={3} onClick={onClose} size="sm">
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                  size="sm"
                >
                  {metricToEdit ? 'Save Changes' : 'Create Metric'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
